import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Server, RotateCw, Calculator, Code, ChevronDown, ChevronUp, Info, CheckCircle2, XCircle, Scan } from 'lucide-react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const IPv6Calculator = () => {
  const [ipv6Prefix, setIpv6Prefix] = useState('64');
  const [ipv6Address, setIpv6Address] = useState('2001:db8:85a3::8a2e:370:7334');
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationResult, setCalculationResult] = useState(null);
  const [error, setError] = useState(null);
  const [expandedSection, setExpandedSection] = useState('summary');
  const [validationIp, setValidationIp] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  const prefixOptions = Array.from({ length: 129 }, (_, i) => i);

  const handleIpv6Submit = async (e) => {
    e.preventDefault();
    setIsCalculating(true);
    setError(null);
    
    try {
      const response = await fetch('/v1/calculate-ipv6-info', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ipAddress: ipv6Address,
          prefix: ipv6Prefix
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to calculate IPv6 information');
      }
      
      setCalculationResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsCalculating(false);
    }
  };

  const clearIpv6Form = () => {
    setIpv6Address('2001:db8:85a3::8a2e:370:7334');
    setIpv6Prefix('64');
    setCalculationResult(null);
    setError(null);
    setValidationResult(null);
    setValidationError(null);
    setValidationIp('');
  };

  const formatIPv6Address = (value) => {
    // Basic validation - remove any spaces
    const formatted = value.replace(/\s/g, '');
    setIpv6Address(formatted);
  };

  const validateIpInRange = async () => {
    if (!calculationResult || !validationIp) return;
    
    setIsValidating(true);
    setValidationError(null);
    
    try {
      // In a real implementation, you would call your backend API
      // For this example, we'll simulate the validation
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Simulate validation logic
      const isValid = Math.random() > 0.3; // 70% chance of being valid for demo
      
      setValidationResult({
        ip: validationIp,
        isValid,
        message: isValid 
          ? 'The IP address is within the calculated subnet range'
          : 'The IP address is NOT within the calculated subnet range'
      });
    } catch (err) {
      setValidationError('Failed to validate IP address');
    } finally {
      setIsValidating(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const addressSpaceChartData = {
    labels: ['Used', 'Available'],
    datasets: [
      {
        label: 'Address Space',
        data: calculationResult ? [
          1,
          Math.log10(calculationResult.total_ip_addresses)
        ] : [0, 0],
        backgroundColor: [
          'rgba(74, 222, 128, 0.8)',
          'rgba(96, 165, 250, 0.8)',
        ],
        borderColor: [
          'rgba(74, 222, 128, 1)',
          'rgba(96, 165, 250, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Address Space Utilization (Logarithmic)',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Log10(Number of Addresses)'
        }
      }
    },
  };

  return (
    <motion.div
      key="ipv6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-8"
    >
      <div className="bg-gray-800/80 rounded-xl shadow-2xl p-6 backdrop-blur-sm border border-gray-700">
        <div className="flex items-center justify-between mb-6">
          <motion.h1 
            className="text-2xl font-bold flex items-center space-x-3"
            initial={{ x: -20 }}
            animate={{ x: 0 }}
          >
            <Server className="h-6 w-6 text-green-400" />
            <span>IPv6 Subnet Calculator</span>
          </motion.h1>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={clearIpv6Form}
            className="flex items-center space-x-2 bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg text-sm transition-colors"
          >
            <RotateCw className="h-4 w-4" />
            <span>Reset</span>
          </motion.button>
        </div>
        
        <form onSubmit={handleIpv6Submit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center">
                <span>IPv6 Address</span>
                <span className="ml-2 text-xs text-gray-400 flex items-center">
                  <Info className="h-3 w-3 mr-1" /> Full or compressed format
                </span>
              </label>
              <input
                type="text"
                value={ipv6Address}
                onChange={(e) => formatIPv6Address(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500/50 focus:border-green-500 focus:outline-none transition-all font-mono"
                placeholder="e.g. 2001:db8::1 or 2001:0db8:0000:0000:0000:0000:0000:0001"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center">
                <span>Prefix Length</span>
                <span className="ml-2 text-xs text-gray-400 flex items-center">
                  <Info className="h-3 w-3 mr-1" /> /0 to /128
                </span>
              </label>
              <select
                value={ipv6Prefix}
                onChange={(e) => setIpv6Prefix(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500/50 focus:border-green-500 focus:outline-none transition-all"
              >
                {prefixOptions.map((prefix) => (
                  <option key={prefix} value={prefix}>/{prefix}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="pt-4">
            <motion.button
              type="submit"
              disabled={isCalculating}
              whileHover={{ scale: isCalculating ? 1 : 1.05 }}
              whileTap={{ scale: isCalculating ? 1 : 0.95 }}
              className={`w-full py-3 px-6 rounded-xl flex items-center justify-center space-x-3 transition-all ${isCalculating ? 'bg-green-600/50 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 shadow-lg'}`}
            >
              {isCalculating ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="h-5 w-5"
                  >
                    <RotateCw className="h-5 w-5" />
                  </motion.div>
                  <span>Calculating...</span>
                </>
              ) : (
                <>
                  <Calculator className="h-5 w-5" />
                  <span>Calculate Subnet</span>
                </>
              )}
            </motion.button>
          </div>
        </form>
      </div>
      
      {error && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-900/20 rounded-xl shadow-2xl p-6 backdrop-blur-sm border border-red-700"
        >
          <div className="flex items-center space-x-3 text-red-400">
            <XCircle className="h-6 w-6" />
            <h2 className="text-xl font-bold">Calculation Error</h2>
          </div>
          <p className="mt-3 text-red-300">{error}</p>
        </motion.div>
      )}
      
      {calculationResult && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800/80 rounded-xl shadow-2xl p-6 backdrop-blur-sm border border-gray-700 space-y-6"
        >
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center space-x-3">
              <Code className="h-5 w-5 text-green-400" />
              <span>IPv6 Subnet Analysis</span>
            </h2>
            <div className="text-sm text-gray-400">
              <span className="font-mono bg-gray-900/50 px-3 py-1 rounded-lg">
                {calculationResult.ipv6_input}
              </span>
            </div>
          </div>
          
          {/* Summary Section */}
          <div className="bg-gray-900/50 rounded-xl border border-gray-700 overflow-hidden">
            <button 
              onClick={() => toggleSection('summary')}
              className="w-full flex justify-between items-center p-4 hover:bg-gray-800/50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <div className="bg-green-500/10 p-2 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-green-400" />
                </div>
                <h3 className="font-semibold">Subnet Summary</h3>
              </div>
              {expandedSection === 'summary' ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </button>
            
            {expandedSection === 'summary' && (
              <div className="p-4 pt-0 space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <p className="text-gray-400 text-sm">Network Address</p>
                    <p className="text-lg font-mono text-green-400">{calculationResult.network_address}</p>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <p className="text-gray-400 text-sm">Prefix Length</p>
                    <p className="text-xl font-mono">/{calculationResult.prefix_length}</p>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <p className="text-gray-400 text-sm">Address Type</p>
                    <p className="text-xl">{calculationResult.ip_type}</p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <p className="text-gray-400 text-sm">Total Addresses</p>
                    <p className="text-2xl font-mono">
                      2<sup className="text-sm">{(128 - parseInt(calculationResult.prefix_length))}</sup>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      ({calculationResult.total_ip_addresses.toExponential(2)} addresses)
                    </p>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <p className="text-gray-400 text-sm">Address Range</p>
                    <p className="font-mono text-green-400 text-sm">{calculationResult.ip_range_from}</p>
                    <p className="font-mono text-green-400 text-sm">{calculationResult.ip_range_to}</p>
                  </div>
                </div>
                
                <div className="h-64">
                  <Bar data={addressSpaceChartData} options={chartOptions} />
                </div>
              </div>
            )}
          </div>
          
          {/* Technical Details Section */}
          <div className="bg-gray-900/50 rounded-xl border border-gray-700 overflow-hidden">
            <button 
              onClick={() => toggleSection('technical')}
              className="w-full flex justify-between items-center p-4 hover:bg-gray-800/50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <div className="bg-blue-500/10 p-2 rounded-lg">
                  <Code className="h-5 w-5 text-blue-400" />
                </div>
                <h3 className="font-semibold">Technical Details</h3>
              </div>
              {expandedSection === 'technical' ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </button>
            
            {expandedSection === 'technical' && (
              <div className="p-4 pt-0 grid md:grid-cols-2 gap-4">
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <p className="text-gray-400 text-sm">Special Addresses</p>
                  <p className="text-sm">{calculationResult.special_addresses}</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <p className="text-gray-400 text-sm">EUI-64 Identifier</p>
                  <p className="text-sm">{calculationResult.eui64}</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <p className="text-gray-400 text-sm">Multihoming</p>
                  <p className="text-sm">{calculationResult.multihoming}</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <p className="text-gray-400 text-sm">Autoconfiguration</p>
                  <p className="text-sm">{calculationResult.autoconfiguration}</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <p className="text-gray-400 text-sm">Address Types</p>
                  <p className="text-sm">{calculationResult.address_types}</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <p className="text-gray-400 text-sm">CIDR Notation</p>
                  <p className="font-mono">{calculationResult.cidr_notation}</p>
                </div>
              </div>
            )}
          </div>
          
          {/* IP Validation Section */}
          <div className="bg-gray-900/50 rounded-xl border border-gray-700 overflow-hidden">
            <button 
              onClick={() => toggleSection('validation')}
              className="w-full flex justify-between items-center p-4 hover:bg-gray-800/50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <div className="bg-purple-500/10 p-2 rounded-lg">
                  <Scan className="h-5 w-5 text-purple-400" />
                </div>
                <h3 className="font-semibold">IP Range Validation</h3>
              </div>
              {expandedSection === 'validation' ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </button>
            
            {expandedSection === 'validation' && (
              <div className="p-4 pt-0 space-y-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Validate IP Address in Range
                    </label>
                    <input
                      type="text"
                      value={validationIp}
                      onChange={(e) => setValidationIp(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 focus:outline-none transition-all font-mono"
                      placeholder="Enter IPv6 address to validate"
                    />
                  </div>
                  <div className="flex items-end">
                    <motion.button
                      onClick={validateIpInRange}
                      disabled={!validationIp || isValidating}
                      whileHover={{ scale: isValidating ? 1 : 1.05 }}
                      whileTap={{ scale: isValidating ? 1 : 0.95 }}
                      className={`py-3 px-6 rounded-lg flex items-center justify-center space-x-2 transition-all ${isValidating ? 'bg-purple-600/50 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'}`}
                    >
                      {isValidating ? (
                        <>
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="h-4 w-4"
                          >
                            <RotateCw className="h-4 w-4" />
                          </motion.div>
                          <span>Validating...</span>
                        </>
                      ) : (
                        <>
                          <Scan className="h-4 w-4" />
                          <span>Validate</span>
                        </>
                      )}
                    </motion.button>
                  </div>
                </div>
                
                {validationError && (
                  <div className="bg-red-900/20 rounded-lg p-4 border border-red-700">
                    <div className="flex items-center space-x-2 text-red-400">
                      <XCircle className="h-4 w-4" />
                      <span>{validationError}</span>
                    </div>
                  </div>
                )}
                
                {validationResult && (
                  <div className={`rounded-lg p-4 border ${validationResult.isValid ? 'bg-green-900/20 border-green-700' : 'bg-red-900/20 border-red-700'}`}>
                    <div className="flex items-center space-x-3">
                      {validationResult.isValid ? (
                        <CheckCircle2 className="h-5 w-5 text-green-400" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-400" />
                      )}
                      <div>
                        <p className="font-mono text-sm">{validationResult.ip}</p>
                        <p className={validationResult.isValid ? 'text-green-300' : 'text-red-300'}>
                          {validationResult.message}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <p className="text-gray-400 text-sm mb-2">Calculated Subnet Range:</p>
                  <p className="font-mono text-green-400 text-sm">{calculationResult.ip_range_from}</p>
                  <p className="font-mono text-green-400 text-sm">{calculationResult.ip_range_to}</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default IPv6Calculator;